import { useState, useEffect, useCallback, useMemo } from 'react';
import { listLessons, listPages } from '../api/assetServiceClient';
import type { LessonListItem, PageListItem } from '../types/catalog';
import type { TocUnit, TocItem } from '../components/CourseTocDrawer';

export interface OutlineFlatItem {
  lessonId: string;
  lessonTitle: string;
  lesson: LessonListItem;
  pageId: string;
  pageTitle: string;
  page: PageListItem;
  unitNo: number;
  lessonNo: number;
  pageNo: number;
  index: number;
  isLocked: boolean;
}

interface OutlineRawItem {
  lessonId: string;
  lessonTitle: string;
  lesson: LessonListItem;
  pageId: string;
  pageTitle: string;
  page: PageListItem;
  unitNo: number;
  lessonNo: number;
  pageNo: number;
  index: number;
}

/**
 * Fetch lessons, then for each lesson fetch pages.
 * Group by unit_no, build flat ordered list for sequential locking.
 */
export function useCourseOutline(
  courseId: string | undefined,
  completedPageIds: string[],
  selectedPageId: string | null
) {
  const [rawItems, setRawItems] = useState<OutlineRawItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!courseId) {
      setRawItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    listLessons(courseId)
      .then(async (lessons) => {
        const pagesByLesson: Map<string, PageListItem[]> = new Map();
        await Promise.all(
          lessons.map(async (l) => {
            const pages = await listPages(l.id);
            pagesByLesson.set(l.id, pages);
          })
        );
        const items: OutlineRawItem[] = [];
        let index = 0;
        const sortedLessons = [...lessons].sort((a, b) => {
          const ua = a.unit_no ?? 0;
          const ub = b.unit_no ?? 0;
          if (ua !== ub) return ua - ub;
          return (a.lesson_no ?? 0) - (b.lesson_no ?? 0);
        });
        for (const lesson of sortedLessons) {
          const pages = (pagesByLesson.get(lesson.id) ?? []).sort(
            (a, b) => (a.page_no ?? 0) - (b.page_no ?? 0)
          );
          const unitNo = lesson.unit_no ?? 0;
          const lessonNo = lesson.lesson_no ?? 0;
          for (const page of pages) {
            items.push({
              lessonId: lesson.id,
              lessonTitle: lesson.title_ta ?? `Lesson ${lessonNo || lesson.id}`,
              lesson,
              pageId: page.id,
              pageTitle: page.page_title ?? `Page ${page.page_no ?? page.id}`,
              page,
              unitNo,
              lessonNo,
              pageNo: page.page_no ?? 0,
              index: index++,
            });
          }
        }
        setRawItems(items);
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Failed to load outline');
        setRawItems([]);
      })
      .finally(() => setLoading(false));
  }, [courseId]);

  const completedSet = new Set(completedPageIds);

  const flatItems = useMemo((): OutlineFlatItem[] => {
    return rawItems.map((item) => ({ ...item, isLocked: false }));
  }, [rawItems]);

  const buildUnits = useCallback((): TocUnit[] => {
    const unitMap = new Map<number, TocItem[]>();
    // Determine sequential locking behavior
    const seqFlag = import.meta.env.VITE_SEQUENTIAL_LOCKING !== 'false';
    // find first incomplete index
    let firstIncompleteIndex = -1;
    if (seqFlag) {
      firstIncompleteIndex = flatItems.findIndex((it) => !completedSet.has(it.pageId));
      if (firstIncompleteIndex === -1) firstIncompleteIndex = flatItems.length; // all completed
    }

    for (let i = 0; i < flatItems.length; i++) {
      const item = flatItems[i];
      const isCompleted = completedSet.has(item.pageId);
      const isActive = selectedPageId === item.pageId;

      let status: TocItem['status'];
      if (isCompleted) status = 'completed';
      else {
        if (seqFlag) {
          if (i > firstIncompleteIndex) status = 'locked';
          else status = 'incomplete';
        } else {
          status = 'incomplete';
        }
      }

      const tocItem: TocItem = {
        lessonId: item.lessonId,
        lessonTitle: item.lessonTitle,
        pageId: item.pageId,
        pageTitle: item.pageTitle,
        status,
        isActive,
      };
      const list = unitMap.get(item.unitNo) ?? [];
      list.push(tocItem);
      unitMap.set(item.unitNo, list);
    }
    const units: TocUnit[] = [];
    const sortedUnitNos = Array.from(unitMap.keys()).sort((a, b) => a - b);
    for (const unitNo of sortedUnitNos) {
      units.push({
        unitNo,
        title: `Unit ${unitNo}`,
        items: unitMap.get(unitNo) ?? [],
      });
    }
    return units;
  }, [flatItems, completedSet, selectedPageId]);

  const units = buildUnits();

  return { units, flatItems, loading, error };
}
